/**
 * SVG line-icons for diary categories.
 * Stroke-based, stroke-width 1.8, stroke-linecap round per brand spec.
 * NO emoji — renders differ per OS and don't match the brand.
 */

interface IconProps {
  size?: number
  color?: string
  className?: string
}

const iconProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const

// Bliksem (lightning bolt) — migraine
export function MigraineIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <path d="M13 2L4.5 13H12L11 22L19.5 11H12L13 2Z" />
    </svg>
  )
}

// Gezicht met lichte frons (face) — hoofdpijn
export function HeadacheIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 15C8.5 14 10 13 12 13C14 13 15.5 14 16 15" />
      <line x1="9" y1="9" x2="9.01" y2="9" strokeWidth="2.2" />
      <line x1="15" y1="9" x2="15.01" y2="9" strokeWidth="2.2" />
    </svg>
  )
}

// Bel (bell) — brain fog / migrainekater
export function BrainfogIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <path d="M18 8A6 6 0 0 0 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" />
      <path d="M13.73 21A2 2 0 0 1 10.27 21" />
    </svg>
  )
}

// Ster (star) — klachtenvrij
export function FreeIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
    </svg>
  )
}

// Lock icon — disabled symptom
export function LockIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7A5 5 0 0 1 17 7V11" />
    </svg>
  )
}

// Capsule (pill) — triptan, 45° rotated with 3D shading
export function TriptanIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <defs>
        <linearGradient id="capsuleShine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.08" />
          <stop offset="40%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.08" />
        </linearGradient>
      </defs>
      <g transform="rotate(-45 12 12)">
        <rect x="8" y="2" width="8" height="20" rx="4" fill="url(#capsuleShine)" />
        <line x1="8" y1="12" x2="16" y2="12" />
        <path d="M10 4.5C10 4.5 11 3.5 12 3.5" strokeOpacity="0.4" />
      </g>
    </svg>
  )
}

// Tablet (round paracetamol) — flat cylinder, centered, matching capsule style
export function PainkillerIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <defs>
        <linearGradient id="tabletShine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.08" />
          <stop offset="40%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.08" />
        </linearGradient>
      </defs>
      {/* Zijkant — zichtbare onderste boog + zijlijnen */}
      <path d="M4 11L4 14" />
      <path d="M20 11L20 14" />
      <path d="M4 14 A8 3 0 0 0 20 14" fill="url(#tabletShine)" />
      <path d="M4 14 A8 3 0 0 0 20 14" fill="none" />
      {/* Bovenkant ovaal (deksel) */}
      <ellipse cx="12" cy="11" rx="8" ry="3" fill="url(#tabletShine)" />
      <ellipse cx="12" cy="11" rx="8" ry="3" />
      {/* Breukstreep op bovenkant — rechte lijn 45° */}
      <line x1="9" y1="8.5" x2="15" y2="13.5" strokeOpacity="0.5" strokeWidth="1.5" />
      {/* Glans */}
      <path d="M7 9.5C8.5 8.5 10 8.2 12 8.2" strokeOpacity="0.4" />
    </svg>
  )
}

// Pen (edit) icon
export function EditIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="M15 5l4 4" />
    </svg>
  )
}

// Info icon
export function InfoIcon({ size = 24, color = 'currentColor', className }: IconProps) {
  return (
    <svg {...iconProps} width={size} height={size} stroke={color} className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" strokeWidth="2.2" />
    </svg>
  )
}
